import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useScrollToTop } from '../../hooks/useScrollToTop';
import { ROUTES } from '../../constants/routes';
import MagneticButton from './MagneticButton';
import AnimatedArrow from './AnimatedArrow';

// ─── Data ────────────────────────────────────────────────────────────────────

const NAV_LINKS = [
  { name: 'Home', path: ROUTES.HOME },
  {
    name: 'Services',
    path: '#',
    dropdown: [
      { name: 'AWS Consulting',       path: ROUTES.SERVICES.AWS,                         icon: 'bi-amazon',              desc: 'Practitioner-led AWS solutions' },
      { name: 'Azure Consulting',     path: ROUTES.SERVICES.AZURE,                       icon: 'bi-microsoft',           desc: 'Enterprise Azure architectures' },
      { name: 'Cloud Migration',      path: ROUTES.SERVICES.CLOUD_MIGRATION,             icon: 'bi-cloud-arrow-up',    desc: 'Server, DB & workload migration' },
      { name: 'Managed Services',     path: ROUTES.SERVICES.MANAGED_SERVICES,            icon: 'bi-gear-wide-connected', desc: '24/7 proactive IT support' },
      { name: 'Cost Optimization',    path: ROUTES.SERVICES.FINOPS,                      icon: 'bi-cash-coin',           desc: 'FinOps & cloud cost governance' },
      { name: 'Security & Governance',path: ROUTES.SERVICES.SECURITY_GOVERNANCE,         icon: 'bi-shield-check',        desc: 'IAM, compliance & zero-trust' },
      { name: 'GenAI Readiness',      path: ROUTES.SERVICES.GENAI_READINESS,             icon: 'bi-stars',               desc: 'Data foundations & AI infrastructure' },
      { name: 'DevOps & CI/CD',       path: ROUTES.SERVICES.DEVOPS,                      icon: 'bi-terminal',            desc: 'IaC, pipelines & automation' },
      { name: 'Backup & Recovery',    path: ROUTES.SERVICES.BACKUP,                      icon: 'bi-hdd-stack',           desc: 'DR planning & replication' },
      { name: 'Cloud Credit Facility',path: ROUTES.CONTACT,                              icon: 'bi-credit-card-2-front', desc: 'AWS & Azure credit guidance' },
    ],
  },
  { name: 'Solutions', path: '#', dropdown: [
      { name: 'For Startups',        path: ROUTES.SOLUTIONS.STARTUPS,                     icon: 'bi-rocket-takeoff',      desc: 'Build fast, control burn rate' },
      { name: 'For SMBs & MSMEs',    path: ROUTES.SOLUTIONS.SMB_MSME,                     icon: 'bi-building',            desc: 'Legacy to cloud migration' },
      { name: 'For Enterprises',     path: ROUTES.SOLUTIONS.ENTERPRISES,                  icon: 'bi-diagram-3',           desc: 'Governance, FinOps & modernization' },
    ],
  },
  { name: 'Locations', path: '#', dropdown: [
      { name: 'Noida Office',        path: ROUTES.LOCATIONS.NOIDA,                        icon: 'bi-geo-alt',             desc: 'Delhi NCR delivery hub' },
      { name: 'Lucknow Office',      path: ROUTES.LOCATIONS.LUCKNOW,                      icon: 'bi-geo-alt',             desc: 'UP region delivery hub' },
    ],
  },
  { name: 'About',     path: ROUTES.ABOUT     },
  { name: 'Contact',   path: ROUTES.CONTACT   },
  { name: 'Resources', path: ROUTES.RESOURCES },
];

// ─── Component ───────────────────────────────────────────────────────────────

const NavigationBar: React.FC = () => {
  const location   = useLocation();
  const scrollToTop = useScrollToTop();

  // State
  const [scrolled,         setScrolled]         = useState(false);
  const [hidden,           setHidden]           = useState(false);
  const [activeDropdown,   setActiveDropdown]   = useState<string | null>(null);
  const [hoveredLink,      setHoveredLink]      = useState<string | null>(null);
  const [mobileOpen,       setMobileOpen]       = useState(false);
  const [mobileServices,   setMobileServices]   = useState(false);

  // Refs
  const lastScrollY  = useRef(0);
  const navRef       = useRef<HTMLElement>(null);

  // ── Reliable scroll tracking (plain event listener, no framer-motion useScroll) ──
  useEffect(() => {
    const onScroll = () => {
      // Never hide navbar on mobile/tablet widths
      if (window.innerWidth < 1024) {
        setHidden(false);
        setScrolled(window.scrollY > 12);
        return;
      }
      const y = window.scrollY;
      setScrolled(y > 24);
      // hide when scrolling down past 120px, show on any up movement
      if (y > 120 && y > lastScrollY.current) {
        setHidden(true);
        setActiveDropdown(null);
      } else {
        setHidden(false);
      }
      lastScrollY.current = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
    setMobileServices(false);
    setActiveDropdown(null);
  }, [location.pathname]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setActiveDropdown(null);
        setMobileOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleNavClick = () => {
    setMobileOpen(false);
    setActiveDropdown(null);
    scrollToTop();
  };

  return (
    <motion.nav
      ref={navRef as any}
      // Use margin:auto centering — never mix translateX with Framer Motion
      // because Framer controls the full transform string for its y-animation
      style={{
        position: 'fixed',
        top: '1rem',
        left: 0,
        right: 0,
        marginLeft: 'auto',
        marginRight: 'auto',
        width: '95%',
        maxWidth: '80rem',
        zIndex: 50,
        // Pass y as a motion value so Framer doesn't conflict with our styles
      }}
      initial={{ y: 0, opacity: 1 }}
      animate={{ y: hidden ? '-130%' : 0, opacity: hidden ? 0 : 1 }}
      transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
      data-clode-target="primary-navigation"
    >
      {/* ── Pill Shell ── */}
      <div
        style={{
          borderRadius: '9999px',
          transition: 'background 0.3s, box-shadow 0.3s, border-color 0.3s',
          background: scrolled ? 'rgba(0,10,30,0.75)' : 'transparent',
          backdropFilter: scrolled ? 'blur(24px)' : 'none',
          WebkitBackdropFilter: scrolled ? 'blur(24px)' : 'none',
          border: `1px solid ${scrolled ? 'rgba(255,255,255,0.10)' : 'transparent'}`,
          boxShadow: scrolled ? '0 8px 40px rgba(0,0,0,0.30)' : 'none',
        }}
        className="px-4 py-2.5 flex items-center justify-between"
      >
        {/* Logo */}
        <Link to="/" onClick={handleNavClick} className="flex-shrink-0 flex items-center">
          <img
            src="/assets/logobg.png"
            alt="CloudYatra Enterprise"
            className="h-10 md:h-12 w-auto object-contain"
          />
        </Link>

        {/* ── Desktop Nav ── */}
        <div
          className="hidden lg:flex items-center gap-1 relative"
          onMouseLeave={() => { setHoveredLink(null); setActiveDropdown(null); }}
        >
          {/* Shared sliding-pill background — always rendered, moves via layoutId */}
          <AnimatePresence>
            {hoveredLink && (
              <motion.span
                key={hoveredLink}
                layoutId="nav-pill"
                className="absolute inset-0 rounded-full pointer-events-none"
                style={{ background: 'rgba(255,255,255,0.08)' }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ type: 'spring', stiffness: 380, damping: 30 }}
              />
            )}
          </AnimatePresence>

          {NAV_LINKS.map((link) => {
            const isActive = link.path !== '#' && location.pathname === link.path;
            return (
              <div
                key={link.name}
                className="relative"
                onMouseEnter={() => {
                  setHoveredLink(link.name);
                  if (link.dropdown) setActiveDropdown(link.name);
                  else setActiveDropdown(null);
                }}
              >
                <Link
                  to={link.path === '/services' ? '#' : link.path}
                  onClick={link.dropdown ? undefined : handleNavClick}
                  className="relative flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-full select-none"
                  style={{
                    color: isActive ? '#fff' : 'rgba(203,213,225,1)',
                    transition: 'color 0.2s',
                    textDecoration: 'none',
                  }}
                >
                  {link.name}
                  {link.dropdown && (
                    <motion.i
                      className="bi bi-chevron-down text-[10px]"
                      animate={{ rotate: activeDropdown === link.name ? 180 : 0 }}
                      transition={{ duration: 0.2 }}
                    />
                  )}
                  {/* Active dot */}
                  {isActive && (
                    <motion.span
                      layoutId="active-dot"
                      className="absolute bottom-1 left-1/2 w-1 h-1 rounded-full bg-blue-500"
                      style={{ x: '-50%' }}
                    />
                  )}
                </Link>

                {/* ── Mega-menu ── */}
                {link.dropdown && (
                  <AnimatePresence>
                    {activeDropdown === link.name && (
                      <motion.div
                        initial={{ opacity: 0, y: 12, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0,  scale: 1    }}
                        exit={{    opacity: 0, y: 12, scale: 0.97 }}
                        transition={{ duration: 0.18, ease: [0.4, 0, 0.2, 1] }}
                        style={{
                          position: 'absolute',
                          top: 'calc(100% + 14px)',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          width: link.name === 'Services' ? '700px' : '480px',
                          background: 'rgba(3,10,30,0.96)',
                          backdropFilter: 'blur(28px)',
                          WebkitBackdropFilter: 'blur(28px)',
                          border: '1px solid rgba(255,255,255,0.09)',
                          borderRadius: '20px',
                          boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
                          padding: '1.5rem',
                          zIndex: 100,
                        }}
                      >
                        <div className={link.name === 'Solutions' ? 'grid grid-cols-1 gap-3' : 'grid grid-cols-2 gap-3'}>
                          {link.dropdown.map((item) => (
                            <Link
                              key={item.name}
                              to={item.path}
                              onClick={handleNavClick}
                              className="group flex items-center gap-3 p-3 rounded-xl"
                              style={{
                                textDecoration: 'none',
                                transition: 'background 0.18s',
                              }}
                              onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.05)')}
                              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                            >
                              <div
                                className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-xl"
                                style={{
                                  background: 'rgba(37,99,235,0.12)',
                                  color: '#60a5fa',
                                  transition: 'background 0.18s, color 0.18s',
                                }}
                                onMouseEnter={e => {
                                  (e.currentTarget as HTMLElement).style.background = '#2563eb';
                                  (e.currentTarget as HTMLElement).style.color = '#fff';
                                }}
                                onMouseLeave={e => {
                                  (e.currentTarget as HTMLElement).style.background = 'rgba(37,99,235,0.12)';
                                  (e.currentTarget as HTMLElement).style.color = '#60a5fa';
                                }}
                              >
                                <i className={`bi ${item.icon} text-base`} />
                              </div>
                              <div>
                                <p className="text-sm font-semibold text-white leading-snug">{item.name}</p>
                                <p className="text-xs text-slate-400 leading-snug mt-0.5">{item.desc}</p>
                              </div>
                            </Link>
                          ))}
                        </div>

                        <div
                          className="flex items-center justify-between mt-5 pt-4"
                          style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}
                        >
                          <p className="text-xs text-slate-400">Not sure what you need?</p>
                          <Link
                            to="/contact"
                            onClick={handleNavClick}
                            className="flex items-center gap-1.5 text-xs font-medium text-blue-400 hover:text-white"
                            style={{ textDecoration: 'none', transition: 'color 0.2s' }}
                          >
                            Talk to an expert
                            <AnimatedArrow isHovered className="w-3 h-3" />
                          </Link>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                )}
              </div>
            );
          })}

          {/* Divider */}
          <div className="w-px h-5 mx-1" style={{ background: 'rgba(255,255,255,0.12)' }} />

          {/* CTA Button */}
          <MagneticButton to="/portal" variant="primary" showArrow className="!px-5 !py-2 ml-1 text-sm" disabled>
            Go to Portal
          </MagneticButton>
        </div>

        {/* ── Mobile Hamburger ── */}
        <button
          className="lg:hidden flex flex-col justify-center items-end gap-1.5 w-9 h-9 p-1"
          style={{ flexShrink: 0 }}
          onClick={() => setMobileOpen(o => !o)}
          aria-label="Toggle navigation"
        >
          <motion.span
            animate={mobileOpen ? { rotate: 45, y: 8 } : { rotate: 0, y: 0 }}
            className="block rounded-full"
            style={{ display: 'block', originX: 0.5, height: '2px', backgroundColor: '#ffffff', width: '100%' }}
            transition={{ duration: 0.22 }}
          />
          <motion.span
            animate={mobileOpen ? { opacity: 0, scaleX: 0 } : { opacity: 1, scaleX: 0.65 }}
            className="block rounded-full"
            style={{ display: 'block', originX: 1, height: '2px', backgroundColor: '#ffffff', width: '100%' }}
            transition={{ duration: 0.22 }}
          />
          <motion.span
            animate={mobileOpen ? { rotate: -45, y: -8 } : { rotate: 0, y: 0 }}
            className="block rounded-full"
            style={{ display: 'block', originX: 0.5, height: '2px', backgroundColor: '#ffffff', width: '100%' }}
            transition={{ duration: 0.22 }}
          />
        </button>
      </div>

      {/* ── Mobile Menu ── */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0,  scale: 1    }}
            exit={{    opacity: 0, y: -8, scale: 0.98 }}
            transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
            className="mt-2 overflow-hidden"
            style={{
              borderRadius: '20px',
              background: 'rgba(3,10,30,0.97)',
              backdropFilter: 'blur(28px)',
              WebkitBackdropFilter: 'blur(28px)',
              border: '1px solid rgba(255,255,255,0.09)',
              boxShadow: '0 24px 60px rgba(0,0,0,0.4)',
              padding: '1rem',
            }}
          >
            <div className="flex flex-col gap-1 max-h-[70vh] overflow-y-auto">
              {NAV_LINKS.map((link) =>
                link.dropdown ? (
                  <div key={link.name}>
                    <button
                      className="w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm font-semibold text-slate-300 uppercase tracking-widest"
                      style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
                      onClick={() => setMobileServices(s => !s)}
                    >
                      {link.name}
                      <motion.i
                        className="bi bi-chevron-down text-xs text-slate-400"
                        animate={{ rotate: mobileServices ? 180 : 0 }}
                        transition={{ duration: 0.2 }}
                      />
                    </button>

                    <AnimatePresence>
                      {mobileServices && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{    height: 0, opacity: 0 }}
                          transition={{ duration: 0.22 }}
                          className="overflow-hidden"
                        >
                          <div className="flex flex-col gap-0.5 pb-2">
                            {link.dropdown.map((item) => (
                              <Link
                                key={item.name}
                                to={item.path}
                                onClick={handleNavClick}
                                className="flex items-center gap-3 px-4 py-2.5 rounded-xl"
                                style={{
                                  textDecoration: 'none',
                                  color: '#e2e8f0',
                                  transition: 'background 0.15s',
                                }}
                                onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.05)')}
                                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                              >
                                <i className={`bi ${item.icon} text-blue-400 w-5 text-center`} />
                                <span className="text-sm font-medium">{item.name}</span>
                              </Link>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <Link
                    key={link.name}
                    to={link.path}
                    onClick={handleNavClick}
                    className="block px-4 py-3 rounded-xl text-base font-medium text-white"
                    style={{
                      textDecoration: 'none',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.05)')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  >
                    {link.name}
                  </Link>
                )
              )}

              <div className="mt-2 pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                <MagneticButton to="/portal" variant="primary" showArrow className="w-full !justify-center !py-3" disabled>
                  Go to Portal
                </MagneticButton>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default NavigationBar;
